/**
 * VolumeHeatmap — GitHub-style calendar showing training density.
 * Shows 16 weeks (4 months) of data. Cell colour = sets logged that day.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';

function getISOWeekDay(d: Date) { return d.getDay() === 0 ? 6 : d.getDay() - 1; } // Mon=0

export default function VolumeHeatmap() {
  const dailyCounts = useLiveQuery(async () => {
    const cutoff = new Date(Date.now() - 112 * 86_400_000).toISOString(); // 16 weeks
    const sets = await db.loggedSets
      .where('loggedAt').aboveOrEqual(cutoff)
      .filter((s) => !s.isWarmup)
      .toArray();
    const map = new Map<string, number>();
    for (const s of sets) {
      const d = s.loggedAt.slice(0, 10);
      map.set(d, (map.get(d) ?? 0) + 1);
    }
    return map;
  }, []) ?? new Map<string, number>();

  // Build a 16-week grid (Mon–Sun columns, week rows)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Align to the most recent Sunday end-of-week
  const dayOfWeek = getISOWeekDay(today); // 0=Mon, 6=Sun
  const gridEnd   = new Date(today.getTime() + (6 - dayOfWeek) * 86_400_000);
  const gridStart = new Date(gridEnd.getTime() - (16 * 7 - 1) * 86_400_000);

  const weeks: { key: string; count: number; isToday: boolean; isFuture: boolean }[][] = [];
  let week: typeof weeks[0] = [];
  const ptr = new Date(gridStart);
  while (ptr <= gridEnd) {
    const key = ptr.toISOString().slice(0, 10);
    const todayKey = today.toISOString().slice(0, 10);
    week.push({ key, count: dailyCounts.get(key) ?? 0, isToday: key === todayKey, isFuture: ptr > today });
    if (week.length === 7) { weeks.push(week); week = []; }
    ptr.setDate(ptr.getDate() + 1);
  }
  if (week.length) weeks.push(week);

  const maxCount = Math.max(...[...dailyCounts.values()], 1);

  function cellColor(count: number, isFuture: boolean) {
    if (isFuture) return 'bg-surface-300 opacity-30';
    if (count === 0) return 'bg-surface-300';
    const t = count / maxCount;
    if (t < 0.25) return 'bg-accent/30';
    if (t < 0.5)  return 'bg-accent/55';
    if (t < 0.75) return 'bg-accent/75';
    return 'bg-accent';
  }

  const monthLabels: { label: string; col: number }[] = [];
  for (let i = 0; i < weeks.length; i++) {
    const firstDay = weeks[i][0].key;
    const d = new Date(firstDay);
    if (d.getDate() <= 7) {
      monthLabels.push({
        label: d.toLocaleDateString('en-US', { month: 'short' }),
        col: i,
      });
    }
  }

  return (
    <section>
      <p className="section-header">Training Volume (16 Weeks)</p>
      <div className="card overflow-x-auto">
        {/* Month labels */}
        <div className="flex gap-1 mb-1 min-w-max">
          {weeks.map((_, i) => {
            const ml = monthLabels.find((m) => m.col === i);
            return (
              <div key={i} className="w-3.5 text-[8px] text-ink-faint text-center">
                {ml?.label ?? ''}
              </div>
            );
          })}
        </div>

        {/* Grid — weeks as columns, days as rows */}
        <div className="flex gap-1 min-w-max">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-1">
              {week.map(({ key, count, isToday, isFuture }) => (
                <div
                  key={key}
                  title={`${key}: ${count} sets`}
                  className={`w-3.5 h-3.5 rounded-sm transition-colors ${cellColor(count, isFuture)}
                              ${isToday ? 'ring-1 ring-accent ring-offset-1 ring-offset-surface-100' : ''}`}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-1.5 mt-3 text-xs text-ink-faint">
          <span>Less</span>
          {['bg-surface-300', 'bg-accent/30', 'bg-accent/55', 'bg-accent/75', 'bg-accent'].map((c) => (
            <div key={c} className={`w-3 h-3 rounded-sm ${c}`} />
          ))}
          <span>More</span>
        </div>
      </div>
    </section>
  );
}
